import React, { useState, useEffect } from 'react';
import { Container, Typography, Box, Paper, TextField, Button, CircularProgress, Alert, Link } from '@mui/material';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import apiClient from '../api'; // Import your API client

function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState(null);

    useEffect(() => {
        try {
            const params = new URLSearchParams(location.search);
            const emailParam = params.get('email');
            if (emailParam) setEmail(emailParam);
        } catch (_) {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);
        setSuccessMessage(null);

        try {
            const response = await apiClient.post('/auth/forgot-password', { email });
            setSuccessMessage(response.data.message || 'Password reset instructions sent (if email exists).');
        } catch (err) {
            console.error("Forgot Password Error:", err);
            setError(err?.response?.data?.message || err.message || 'Failed to send reset instructions.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container component="main" maxWidth="xs">
            <Helmet>
                <title>Kranawetter Driver Portal | Reset Password</title>
                <meta name="description" content="Reset your password for Kranawetter Driver Portal - The comprehensive trucking management system that helps you organize customers, track truck documentation, and convey safety information." />
                <meta property="og:title" content="Kranawetter Driver Portal | Password Reset" />
                <meta property="og:description" content="Access Kranawetter Driver Portal's trucking fleet management tools by resetting your password." />
                <meta property="og:type" content="website" />
                <meta name="robots" content="noindex" />
            </Helmet>
            <Paper elevation={3} sx={{ marginTop: { xs: 2, sm: 4 }, padding: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography component="h1" variant="h5" sx={{ mb: 1 }}>
                    Forgot Password
                </Typography>
                
                {!successMessage ? (
                    <>
                        <Typography variant="body2" sx={{ mb: 1.5, textAlign: 'center' }}>
                            Enter your email address below and we'll send you instructions to reset your password.
                        </Typography>
                        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 0.5, width: '100%' }}>
                            {error && <Alert severity="error" sx={{ mb: 1.5, width: '100%', py: 0.5 }}>{error}</Alert>}
                            <TextField
                                margin="dense"
                                required
                                fullWidth
                                id="email"
                                label="Email Address"
                                name="email"
                                autoComplete="email"
                                autoFocus
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                disabled={loading}
                                variant="outlined"
                                size="small"
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 1.5, mb: 1, py: 1 }}
                                disabled={loading || !email}
                                size="medium"
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : 'Send Reset Instructions'}
                            </Button>
                        </Box>
                    </>
                ) : (
                    <Alert severity="success" sx={{ width: '100%', mt: 1, py: 0.75 }}>
                        {successMessage}
                    </Alert>
                )}
                
                <Typography variant="body2" sx={{ mt: 1.5 }}>
                    <RouterLink to="/login" style={{ textDecoration: 'none', color: 'inherit' }}>
                        Back to Sign In
                    </RouterLink>
                </Typography>
            </Paper>
        </Container>
    );
}

export default ForgotPasswordPage; 